import { useEffect, useMemo, useRef, useState } from 'react'
import { useServerNowMs } from './useServerNowMs'

/** MM:SS from total seconds (floor); same as `GlobalDirectChallengeHost` join-prep display. */
export function formatDirectChallengeJoinPrepMmSs(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(totalSeconds))
  const m = Math.floor(sec / 60)
  const r = sec % 60
  return `${m}:${String(r).padStart(2, '0')}`
}

type UseDirectChallengeJoinPrepRemainingMsArgs = {
  /** When null/undefined, countdown returns 0 and no interval runs. */
  challengeId: string | null | undefined
  joinPrepExpiresAtUnixMs: number | null | undefined
  challengeCreatedAtUnixMs: number | null | undefined
  address: string | null | undefined
}

/**
 * Remaining join-prep ms using the same server-aligned logic as `GlobalDirectChallengeHost`
 * (`ttlMs - (serverNow - challengeCreatedAtUnixMs)` when `createdAt` is present).
 */
export function useDirectChallengeJoinPrepRemainingMs({
  challengeId,
  joinPrepExpiresAtUnixMs,
  challengeCreatedAtUnixMs,
  address,
}: UseDirectChallengeJoinPrepRemainingMsArgs): number {
  const syncedServerNowMs = useServerNowMs(address ?? null)
  const joinPrepTimeSyncRef = useRef({ serverMs: 0, localMs: 0 })
  const [joinPrepUiPulse, setJoinPrepUiPulse] = useState(0)

  useEffect(() => {
    joinPrepTimeSyncRef.current = { serverMs: syncedServerNowMs, localMs: Date.now() }
  }, [syncedServerNowMs])

  useEffect(() => {
    if (!challengeId || !joinPrepExpiresAtUnixMs) return
    const id = window.setInterval(() => setJoinPrepUiPulse((n) => n + 1), 250)
    return () => window.clearInterval(id)
  }, [challengeId, joinPrepExpiresAtUnixMs])

  return useMemo(() => {
    if (!joinPrepExpiresAtUnixMs) return 0
    const { serverMs, localMs } = joinPrepTimeSyncRef.current
    const estimatedServerNow =
      serverMs === 0 && localMs === 0
        ? syncedServerNowMs
        : serverMs + (Date.now() - localMs)

    const started = challengeCreatedAtUnixMs
    if (started != null && Number.isFinite(started)) {
      const ttlMs = joinPrepExpiresAtUnixMs - started
      if (ttlMs > 0) {
        return Math.max(0, ttlMs - (estimatedServerNow - started))
      }
    }
    return Math.max(0, joinPrepExpiresAtUnixMs - estimatedServerNow)
  }, [
    joinPrepExpiresAtUnixMs,
    challengeCreatedAtUnixMs,
    syncedServerNowMs,
    joinPrepUiPulse,
  ])
}
